// Generate PDF content from report data
export const generatePDFContent = async (
  processedHeader,
  processedFooter,
  transcriptContent,
  videoUrl = null,
  introVideoUrl = null,
  language = "en"
) => {
  // Check if language is Hebrew or Arabic for RTL alignment
  const isRTL = language === "he" || language === "ar";
  // Create a temporary div to generate the PDF content
  const tempDiv = document.createElement("div");
  tempDiv.id = "temp-pdf-content";
  tempDiv.className = "prose max-w-none";
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.top = "-9999px";
  tempDiv.style.width = "210mm";
  tempDiv.style.backgroundColor = "white";
  tempDiv.style.padding = "10px"; // Reduced from 20px to 10px
  tempDiv.style.zIndex = "-9999"; // Ensure it's behind everything
  tempDiv.style.pointerEvents = "none"; // Prevent interaction

  // Build the complete report content with the same structure as PreviewModal
  tempDiv.innerHTML = `
    <style scoped>
      #temp-pdf-content .prose { max-width: none !important; }
      #temp-pdf-content table { width: 100% !important; }
      #temp-pdf-content th, #temp-pdf-content td { width: auto !important; text-align: left !important; }
      #temp-pdf-content .tiptap-editor, #temp-pdf-content .tiptap-editor * { color: #111827 !important; }
      #temp-pdf-content .text-gray-900 { color: #111827 !important; }
      #temp-pdf-content .bg-blue-50 { background-color: #eff6ff !important; }
      #temp-pdf-content .border-gray-300 { border-color: #d1d5db !important; }
      #temp-pdf-content .border-t-2 { border-top-width: 2px !important; }
      #temp-pdf-content .text-2xl { font-size: 1.5rem !important; }
      #temp-pdf-content .font-bold { font-weight: 700 !important; }
      #temp-pdf-content .text-sm { font-size: 0.875rem !important; }
      #temp-pdf-content .p-4 { padding: 0.75rem !important; }
      #temp-pdf-content .rounded-lg { border-radius: 0.5rem !important; }
      #temp-pdf-content .grid { display: grid !important; }
      #temp-pdf-content .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
      #temp-pdf-content .gap-4 { gap: 0.75rem !important; }
      #temp-pdf-content .my-4 { margin-top: 0.75rem !important; margin-bottom: 0.75rem !important; }
      #temp-pdf-content .mb-4 { margin-bottom: 0.75rem !important; }
      #temp-pdf-content .mt-4 { margin-top: 0.75rem !important; }
      #temp-pdf-content .flex { display: flex !important; }
      #temp-pdf-content .items-center { align-items: center !important; }
      #temp-pdf-content .justify-center { justify-content: center !important; }
      #temp-pdf-content .w-24 { width: 6rem !important; }
      #temp-pdf-content .text-center { text-align: center !important; }
      #temp-pdf-content .mb-4 { margin-bottom: 1rem !important; }
      #temp-pdf-content .mb-2 { margin-bottom: 0.5rem !important; }
      #temp-pdf-content a { color: #A4C639 !important; text-decoration: none !important; }
      #temp-pdf-content a:hover { text-decoration: underline !important; }
      #temp-pdf-content hr { border: none !important; border-top: 1px solid #d1d5db !important; margin: 0.75rem 0 !important; }
      #temp-pdf-content p { margin: 0.25rem 0 !important; }
      #temp-pdf-content h1, #temp-pdf-content h2, #temp-pdf-content h3, #temp-pdf-content h4, #temp-pdf-content h5, #temp-pdf-content h6 { margin: 0.5rem 0 0.25rem 0 !important; }
      #temp-pdf-content ul, #temp-pdf-content ol { margin: 0.25rem 0 !important; padding-left: 1.5rem !important; }
      #temp-pdf-content li { margin: 0.125rem 0 !important; }
      #temp-pdf-content, #temp-pdf-content * {
        hyphens: none !important;
        -webkit-hyphens: none !important;
        word-break: normal !important;
        overflow-wrap: normal !important;
        word-wrap: normal !important;
      }
      ${isRTL ? `
      #temp-pdf-content .text-gray-900[dir="rtl"] {
        direction: rtl !important;
        text-align: right !important;
      }
      #temp-pdf-content .text-gray-900[dir="rtl"] * {
        direction: rtl !important;
        text-align: right !important;
      }
      #temp-pdf-content .text-gray-900[dir="rtl"] p,
      #temp-pdf-content .text-gray-900[dir="rtl"] div,
      #temp-pdf-content .text-gray-900[dir="rtl"] span,
      #temp-pdf-content .text-gray-900[dir="rtl"] h1,
      #temp-pdf-content .text-gray-900[dir="rtl"] h2,
      #temp-pdf-content .text-gray-900[dir="rtl"] h3,
      #temp-pdf-content .text-gray-900[dir="rtl"] h4,
      #temp-pdf-content .text-gray-900[dir="rtl"] h5,
      #temp-pdf-content .text-gray-900[dir="rtl"] h6 {
        text-align: right !important;
      }
      #temp-pdf-content .text-gray-900[dir="rtl"] table {
        direction: rtl !important;
      }
      #temp-pdf-content .text-gray-900[dir="rtl"] th,
      #temp-pdf-content .text-gray-900[dir="rtl"] td {
        text-align: right !important;
      }
      ` : ''}
    </style>
    
    ${
      processedHeader
        ? `
      <div>
        <div class="text-center text-2xl font-bold my-4 flex items-center justify-center gap-4">
          <span class="border-t-2 border-gray-300 w-24"></span>
          <span class="text-gray-900">FINAL REPORT</span>
          <span class="border-t-2 border-gray-300 w-24"></span>
        </div>
        ${
          videoUrl && videoUrl.trim()
            ? `
        <div class="text-center mb-2">
          <a id="pdf-video-link" href="${videoUrl.trim()}" target="_blank" rel="noopener noreferrer" style="color: #A4C639; font-size: 0.875rem; font-weight: 500;" title="${videoUrl.trim()}">
            ▶ Watch Recording
          </a>
        </div>
        `
            : ""
        }
        ${
          introVideoUrl
            ? `
        <div class="text-center mb-4">
          <a id="pdf-intro-video-link" href="${String(
            introVideoUrl
          ).trim()}" target="_blank" rel="noopener noreferrer" style="color: #A4C639; font-size: 0.875rem; font-weight: 500;" title="${String(
                introVideoUrl
              ).trim()}">
            ▶ Watch Intro Video
          </a>
        </div>
        `
            : ""
        }
        <div class="bg-blue-50 p-4 rounded-lg grid grid-cols-1 gap-4 text-sm text-gray-900">
          <div style="width: 100%">${processedHeader}</div>
        </div>
      </div>
      <hr class="mt-4 border-gray-300" />
    `
        : ""
    }
    
    <div class="text-gray-900" ${isRTL ? 'dir="rtl" style="text-align: right; direction: rtl;"' : ''}>
      ${transcriptContent || ""}
    </div>
    
    ${
      processedFooter
        ? `
      <hr class="border-gray-300" />
      <div class="text-gray-900">
        ${processedFooter}
      </div>
    `
        : ""
    }
  `;

  // Create a container to isolate the PDF generation
  const pdfContainer = document.createElement("div");
  pdfContainer.style.position = "fixed";
  pdfContainer.style.top = "-9999px";
  pdfContainer.style.left = "-9999px";
  pdfContainer.style.width = "1px";
  pdfContainer.style.height = "1px";
  pdfContainer.style.overflow = "hidden";
  pdfContainer.style.zIndex = "-9999";
  pdfContainer.style.pointerEvents = "none";
  pdfContainer.appendChild(tempDiv);

  document.body.appendChild(pdfContainer);

  const inlineRemoteImages = async (root) => {
    const images = Array.from(root.querySelectorAll("img"));
    if (images.length === 0) return;

    const toDataURL = (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    await Promise.all(
      images.map(async (img) => {
        const src = img.getAttribute("src");
        if (!src || src.startsWith("data:")) return;

        try {
          const response = await fetch(src, {
            mode: "cors",
            credentials: "include",
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
          }
          const blob = await response.blob();
          const dataUrl = await toDataURL(blob);
          img.setAttribute("data-original-src", src);
          img.src = dataUrl;

          await new Promise((resolve) => {
            if (img.complete && img.naturalWidth !== 0) {
              resolve();
              return;
            }
            const cleanup = () => {
              img.removeEventListener("load", onLoad);
              img.removeEventListener("error", onError);
              resolve();
            };
            const onLoad = () => cleanup();
            const onError = () => cleanup();
            img.addEventListener("load", onLoad, { once: true });
            img.addEventListener("error", onError, { once: true });
          });
        } catch (error) {
          console.warn("Skipping image for PDF inlining", src, error);
        }
      })
    );
  };

  try {
    const { default: html2canvas } = await import("html2canvas");
    const { default: jsPDF } = await import("jspdf");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const padding = 5; // Reduced from 10 to 5 for more content space
    const contentWidth = pdfWidth - padding * 2;
    const contentHeight = pdfHeight - padding * 2;

    await inlineRemoteImages(tempDiv);
    // start of video link position calculation
    // Find both video link elements to calculate their positions
    let videoLinkElement = null;
    let videoLinkPosition = null;
    let introVideoLinkElement = null;
    let introVideoLinkPosition = null;

    // Wait a bit for DOM to be ready and layout to settle
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Get recording video link position
    if (videoUrl) {
      videoLinkElement = tempDiv.querySelector("#pdf-video-link");
      if (videoLinkElement) {
        // Temporarily make container visible to get accurate measurements
        const originalPosition = pdfContainer.style.position;
        const originalTop = pdfContainer.style.top;
        const originalLeft = pdfContainer.style.left;
        const originalWidth = pdfContainer.style.width;
        const originalHeight = pdfContainer.style.height;

        pdfContainer.style.position = "relative";
        pdfContainer.style.top = "0";
        pdfContainer.style.left = "0";
        pdfContainer.style.width = "210mm";
        pdfContainer.style.height = "auto";

        // Force a reflow
        void pdfContainer.offsetHeight;

        const rect = videoLinkElement.getBoundingClientRect();
        const tempDivRect = tempDiv.getBoundingClientRect();

        videoLinkPosition = {
          x: rect.left - tempDivRect.left,
          y: rect.top - tempDivRect.top,
          width: rect.width,
          height: rect.height,
        };

        // Restore original styles
        pdfContainer.style.position = originalPosition;
        pdfContainer.style.top = originalTop;
        pdfContainer.style.left = originalLeft;
        pdfContainer.style.width = originalWidth;
        pdfContainer.style.height = originalHeight;
      }
    }

    // Get intro video link position
    if (introVideoUrl) {
      introVideoLinkElement = tempDiv.querySelector("#pdf-intro-video-link");
      if (introVideoLinkElement) {
        // Temporarily make container visible to get accurate measurements
        const originalPosition = pdfContainer.style.position;
        const originalTop = pdfContainer.style.top;
        const originalLeft = pdfContainer.style.left;
        const originalWidth = pdfContainer.style.width;
        const originalHeight = pdfContainer.style.height;

        pdfContainer.style.position = "relative";
        pdfContainer.style.top = "0";
        pdfContainer.style.left = "0";
        pdfContainer.style.width = "210mm";
        pdfContainer.style.height = "auto";

        // Force a reflow
        void pdfContainer.offsetHeight;

        const rect = introVideoLinkElement.getBoundingClientRect();
        const tempDivRect = tempDiv.getBoundingClientRect();

        introVideoLinkPosition = {
          x: rect.left - tempDivRect.left,
          y: rect.top - tempDivRect.top,
          width: rect.width,
          height: rect.height,
        };

        // Restore original styles
        pdfContainer.style.position = originalPosition;
        pdfContainer.style.top = originalTop;
        pdfContainer.style.left = originalLeft;
        pdfContainer.style.width = originalWidth;
        pdfContainer.style.height = originalHeight;
      }
    }
    // end of video link position calculation
    // start of full canvas generation
    const fullCanvas = await html2canvas(tempDiv, {
      scale: 1, // Reduced from 2 to 1 for smaller file size
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      imageTimeout: 0,
      removeContainer: true,
      logging: false,
    });

    const fullImgData = fullCanvas.toDataURL("image/jpeg", 0.8); // JPEG with 80% quality
    const fullImgHeight = (fullCanvas.height * contentWidth) / fullCanvas.width;

    const pagesNeeded = Math.ceil(fullImgHeight / contentHeight);
    const forceMultiPage = fullImgHeight > 300; // Force if content is over 300mm

    console.log("PDF Generation Info:", {
      fullImgHeight: fullImgHeight.toFixed(2) + "mm",
      contentHeight: contentHeight.toFixed(2) + "mm",
      pagesNeeded,
      forceMultiPage,
      contentLength: transcriptContent ? transcriptContent.length : 0,
    });

    if (pagesNeeded <= 1 && !forceMultiPage) {
      pdf.addImage(
        fullImgData,
        "JPEG",
        padding,
        padding,
        contentWidth,
        fullImgHeight
      );

      // Add clickable link annotation for recording video
      if (videoUrl && videoLinkPosition) {
        // Convert pixel position to PDF coordinates (mm)
        const scaleX = contentWidth / fullCanvas.width;
        const scaleY = fullImgHeight / fullCanvas.height;
        const linkX = padding + videoLinkPosition.x * scaleX;
        const linkY = padding + videoLinkPosition.y * scaleY;
        const linkWidth = videoLinkPosition.width * scaleX;
        const linkHeight = videoLinkPosition.height * scaleY;

        // Add clickable link annotation
        pdf.link(linkX, linkY, linkWidth, linkHeight, {
          url: videoUrl,
        });
        console.log("Clickable video link added to PDF");
      }

      // Add clickable link annotation for intro video
      if (introVideoUrl && introVideoLinkPosition) {
        // Convert pixel position to PDF coordinates (mm)
        const scaleX = contentWidth / fullCanvas.width;
        const scaleY = fullImgHeight / fullCanvas.height;
        const linkX = padding + introVideoLinkPosition.x * scaleX;
        const linkY = padding + introVideoLinkPosition.y * scaleY;
        const linkWidth = introVideoLinkPosition.width * scaleX;
        const linkHeight = introVideoLinkPosition.height * scaleY;

        // Add clickable link annotation
        pdf.link(linkX, linkY, linkWidth, linkHeight, {
          url: introVideoUrl,
        });
      }
    } else {
      const pageHeightPx = Math.max(
        1,
        Math.floor((contentHeight * fullCanvas.width) / contentWidth)
      );
      let position = 0;
      let pageIndex = 0;

      while (position < fullCanvas.height) {
        const sliceHeightPx = Math.min(
          pageHeightPx,
          fullCanvas.height - position
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = fullCanvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          fullCanvas,
          0,
          position,
          fullCanvas.width,
          sliceHeightPx,
          0,
          0,
          fullCanvas.width,
          sliceHeightPx
        );

        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.8);
        const pageImgHeight = (sliceHeightPx * contentWidth) / fullCanvas.width;

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          pageImgData,
          "JPEG",
          padding,
          padding,
          contentWidth,
          pageImgHeight
        );

        // Add clickable link annotations on first page if video links exist
        if (pageIndex === 0) {
          const firstPageHeightPx = Math.min(pageHeightPx, fullCanvas.height);

          // Add recording video link
          if (videoUrl && videoLinkPosition) {
            const linkYInPx = videoLinkPosition.y;
            if (linkYInPx < firstPageHeightPx) {
              // Convert pixel position to PDF coordinates (mm)
              const scaleX = contentWidth / fullCanvas.width;
              const scaleY = pageImgHeight / sliceHeightPx;
              const linkX = padding + videoLinkPosition.x * scaleX;
              const linkY = padding + videoLinkPosition.y * scaleY;
              const linkWidth = videoLinkPosition.width * scaleX;
              const linkHeight = videoLinkPosition.height * scaleY;

              // Add clickable link annotation
              pdf.link(linkX, linkY, linkWidth, linkHeight, {
                url: videoUrl,
              });
            }
          }

          // Add intro video link
          if (introVideoUrl && introVideoLinkPosition) {
            const linkYInPx = introVideoLinkPosition.y;
            if (linkYInPx < firstPageHeightPx) {
              // Convert pixel position to PDF coordinates (mm)
              const scaleX = contentWidth / fullCanvas.width;
              const scaleY = pageImgHeight / sliceHeightPx;
              const linkX = padding + introVideoLinkPosition.x * scaleX;
              const linkY = padding + introVideoLinkPosition.y * scaleY;
              const linkWidth = introVideoLinkPosition.width * scaleX;
              const linkHeight = introVideoLinkPosition.height * scaleY;

              // Add clickable link annotation
              pdf.link(linkX, linkY, linkWidth, linkHeight, {
                url: introVideoUrl,
              });
            }
          }
        }

        position += sliceHeightPx;
        pageIndex += 1;
      }
    }

    const pdfBase64 = pdf.output("datauristring").split(",")[1];

    // Fallback to text if PDF is too large
    const maxSize = 5 * 1024 * 1024;
    const estimatedSize = pdfBase64.length * 0.75;

    if (estimatedSize > maxSize) {
      console.warn("PDF too large, falling back to text content");
      const textContent = `
        ${processedHeader ? `HEADER:\n${processedHeader}\n\n` : ""}
        CONTENT:\n${transcriptContent || ""}
        ${processedFooter ? `\n\nFOOTER:\n${processedFooter}` : ""}
      `.trim();
      return btoa(unescape(encodeURIComponent(textContent)));
    } else {
      return pdfBase64;
    }
  } catch (error) {
    console.error("Error generating PDF:", error);
    console.warn("PDF generation failed, falling back to text content");
    const textContent = `
      ${processedHeader ? `HEADER:\n${processedHeader}\n\n` : ""}
      CONTENT:\n${transcriptContent || ""}
      ${processedFooter ? `\n\nFOOTER:\n${processedFooter}` : ""}
    `.trim();
    return btoa(unescape(encodeURIComponent(textContent)));
  } finally {
    // Cleanup all temporary DOM elements
    console.log("Cleaning up PDF generation elements...");

    try {
      if (document.body.contains(pdfContainer)) {
        document.body.removeChild(pdfContainer);
        console.log("Removed main PDF container");
      }
    } catch (error) {
      console.warn("Error removing main PDF container:", error);
    }

    console.log("PDF generation cleanup completed");
  }
};
